import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react'

import { useIncidentWithDisplayId } from '../hooks/useIncidentWithDisplayId'
import { useCanBulkAction } from '@/hooks/useBulkAction'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { useOpenIncident } from '@/hooks/useOpenIncident'
import {
  PaginationState,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table'
import { useRouter } from 'next/compat/router'
import { useTranslation } from 'react-i18next'

import { Box, useDisclosure } from '@chakra-ui/react'

import {
  ActionBar,
  AiOperatorIcon,
  ResolveIcon,
  TABLE_HEIGHT,
  TABLE_MAX_WIDTH,
  Table,
  TableBody,
  TablePagination,
} from '@hivewatch/dark'

import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { setIncidentsPageSize } from '@/redux/incidents/incidentsSlice'
import {
  selectIncidentDrawerNavigationContext,
  selectIsIncidentDrawerOpen,
  setIncidentDrawerNavigationContext,
} from '@/redux/ui/uiSlice'
import { AI_INCIDENT_SUMMARY_BUTTON_PRESS, trackEvent } from '@/utils/analytics'

import { DarkBulkActionModal } from '../components/DarkBulkActionModal'
import { DarkSummarizeIncidentsModal } from '../components/DarkSummarizeIncidentsModal'
import { IncidentsListType } from '../types/types'
import { canBulkResolveIncidents, formatTableData } from '../utils/helpers'
import { useDarkIncidentColumns } from './components/DarkIncidentsColumns'
import { IncidentsTableHeader } from './components/IncidentsTableHeader'
import { IncidentsTableRow } from './components/IncidentsTableRow'

// Preserves the exact legacy Mixpanel event fired when a row is clicked.
const INCIDENT_ROW_PRESS = 'Button Press: Incident Row, Incidents View'

const ROW_HEIGHT = 56

interface DarkIncidentsTableIProps {
  incidents?: IncidentsListType[]
  /** Total across all pages. Undefined when the query reports no count. */
  totalIncidents?: number
  /** Whether a further page exists. Only set when `totalIncidents` is not. */
  hasNextPage?: boolean
  isLoading?: boolean
  paginationState: PaginationState
  setPaginationState: Dispatch<SetStateAction<PaginationState>>
  sorting: SortingState
  setSorting: Dispatch<SetStateAction<SortingState>>
  rowSelection: RowSelectionState
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>
  isPriorityQueueActive?: boolean
}

/**
 * Rebranded Incidents table (ENG-30677). Rendered in place of `IncidentsTable`
 * when `FeatureFlag.incidentDrawerRebrand` is on. Applies Design Library styling
 * via the `@hivewatch/dark` Table primitives and reorders columns so Status sits
 * immediately right of Priority. Selection state, drawer navigation and all
 * instrumentation are preserved from the legacy table.
 */
export const DarkIncidentsTable = ({
  incidents,
  totalIncidents,
  hasNextPage,
  isLoading = false,
  paginationState,
  setPaginationState,
  // Otherwise unconsumed: the dark Table owns its sort UI state internally via
  // uncontrolled clicks, and we only push changes up via `setSorting`. Read
  // here only to seed the controlled activeColumn/activeOrder pair while
  // Priority Queue is active (see below).
  sorting,
  setSorting,
  rowSelection,
  setRowSelection,
  isPriorityQueueActive = false,
}: DarkIncidentsTableIProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { pageIndex, pageSize } = paginationState
  // Stays undefined without a total — coalescing to 0 here would render "no
  // count available" as "no results" and disable the next control outright.
  const pageCount =
    totalIncidents != null ? Math.ceil(totalIncidents / pageSize) : undefined

  const { openIncident, setDisplayId, openAlertIncidentDrawer } =
    useOpenIncident()
  const { query } = useRouter()
  const isDrawerOpen = useAppSelector(selectIsIncidentDrawerOpen)
  const incidentDrawerNavigationContext = useAppSelector(
    selectIncidentDrawerNavigationContext
  )
  const { queryIncidentId } = useIncidentWithDisplayId({
    displayId: query?.displayId,
  })

  const isQuickReportWhiteListEnabled = useFeatureFlag(
    FeatureFlag.quickReportWhitelist
  )
  const canBulkAction = useCanBulkAction()
  const isAiOperatorEnabled = useFeatureFlag(FeatureFlag.aiOperator)
  const isPriorityQueueEnabled = useFeatureFlag(FeatureFlag.priorityQueue)
  const isAiIncidentSummaryEnabled = useFeatureFlag(
    FeatureFlag.aiIncidentSummary
  )

  const {
    isOpen: isBulkModalOpen,
    onOpen: onBulkModalOpen,
    onClose: onBulkModalClose,
  } = useDisclosure()

  const {
    isOpen: isSummarizeModalOpen,
    onOpen: onSummarizeModalOpen,
    onClose: onSummarizeModalClose,
  } = useDisclosure()

  const rows = formatTableData(incidents, isQuickReportWhiteListEnabled)

  const navigationIncidents = useMemo(
    () =>
      (incidents ?? [])
        .filter((incident) => Boolean(incident?.id && incident?.displayId))
        .map((incident) => ({
          id: incident.id,
          displayId: incident.displayId,
        })),
    [incidents]
  )

  // region Selection (kept in the lifted RowSelectionState so page-level
  // filter/pagination resets continue to clear it)
  const selectedIncidentIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection]
  )
  const selectedIncidents = useMemo(
    () =>
      (incidents ?? []).filter((incident) =>
        selectedIncidentIds.includes(incident.id)
      ),
    [incidents, selectedIncidentIds]
  )
  const canBulkResolve = canBulkResolveIncidents(
    selectedIncidents,
    selectedIncidentIds.length
  )
  const isAllSelected =
    rows.length > 0 && rows.every((row) => rowSelection[row.id])
  const isSomeSelected =
    !isAllSelected && rows.some((row) => rowSelection[row.id])

  const clearSelection = () => setRowSelection({})

  const toggleRow = (id: string) =>
    setRowSelection((prev) => {
      const next = { ...prev }
      if (next[id]) {
        delete next[id]
      } else {
        next[id] = true
      }
      return next
    })

  const toggleAll = () => {
    if (isAllSelected) {
      clearSelection()
      return
    }
    const next: RowSelectionState = {}
    rows.forEach((row) => {
      next[row.id] = true
    })
    setRowSelection(next)
  }
  // endregion

  // region Drawer navigation context (mirrors legacy IncidentsTable)
  useEffect(() => {
    if (queryIncidentId) {
      dispatch(
        setIncidentDrawerNavigationContext({
          isFromIncidentsTable: true,
          incidents: navigationIncidents,
        })
      )
      openAlertIncidentDrawer(queryIncidentId)
    }
  }, [dispatch, openAlertIncidentDrawer, queryIncidentId])

  // Rebuild navigation context after a refresh clears it, so prev/next still
  // works while the drawer is open from a `displayId` in the URL.
  useEffect(() => {
    if (
      !isDrawerOpen ||
      !incidentDrawerNavigationContext?.isFromIncidentsTable
    ) {
      return
    }
    dispatch(
      setIncidentDrawerNavigationContext({
        isFromIncidentsTable: true,
        incidents: navigationIncidents,
      })
    )
  }, [
    dispatch,
    isDrawerOpen,
    incidentDrawerNavigationContext?.isFromIncidentsTable,
    navigationIncidents,
  ])

  // Clear the URL param when the drawer closes.
  const prevDrawerOpenState = useRef(isDrawerOpen)
  useEffect(() => {
    if (!isDrawerOpen && prevDrawerOpenState.current) {
      setDisplayId(null)
    }
    prevDrawerOpenState.current = isDrawerOpen
  }, [isDrawerOpen, setDisplayId])
  // endregion

  const handleRowClick = (id: string) => {
    const selectedIncident = incidents?.find((inc) => inc.id === id)
    dispatch(
      setIncidentDrawerNavigationContext({
        isFromIncidentsTable: true,
        incidents: navigationIncidents,
      })
    )
    trackEvent(INCIDENT_ROW_PRESS, {
      incident_id: selectedIncident?.displayId,
      incident_status: selectedIncident?.status,
      incident_type: selectedIncident?.status,
    })
    openIncident({
      incidentId: id,
      displayId: selectedIncident?.displayId,
    })
  }

  const isActionBarVisible = selectedIncidentIds.length > 0 && canBulkAction

  const tableHeight = `min(${TABLE_HEIGHT}, ${
    40 + (rows.length || (isLoading ? pageSize : 1)) * ROW_HEIGHT + ROW_HEIGHT
  }px)`

  const columns = useDarkIncidentColumns({
    colorScheme,
    showCheckbox: canBulkAction,
    showFlag: isAiOperatorEnabled,
    isFacilityFloorSortDisabled: isPriorityQueueEnabled,
    isPriorityQueueActive,
    isAllSelected,
    isSomeSelected,
    onToggleAll: toggleAll,
    isRowSelected: (id) => Boolean(rowSelection[id]),
    onToggleRow: toggleRow,
  })
  const sortableColumns = useMemo(
    () =>
      columns
        .filter((column) => column.sortColumn)
        .map((column) => column.sortColumn as string),
    [columns]
  )

  // Always controlled from the shared `sorting` state (rather than only while
  // Priority Queue is active) so Table's own internal sort state is never used
  // and can't go stale across a controlled/uncontrolled transition.
  const activeColumn = sorting?.[0]?.id ?? null
  const activeOrder = sorting?.[0] ? (sorting[0].desc ? 'desc' : 'asc') : null

  return (
    <>
      <Box textAlign='left'>
        <Table
          activeColumn={activeColumn}
          activeOrder={activeOrder}
          colorScheme={colorScheme}
          disableSortClear={isPriorityQueueActive}
          height={tableHeight}
          maxWidth={TABLE_MAX_WIDTH}
          onSortChange={(column, order) => {
            setSorting(
              column && order ? [{ id: column, desc: order === 'desc' }] : []
            )
            setPaginationState((prev) => ({ ...prev, pageIndex: 0 }))
            clearSelection()
          }}
          sortableColumns={sortableColumns}
        >
          <IncidentsTableHeader columns={columns} />
          <TableBody isLoading={isLoading} skeletonRowCount={pageSize}>
            {rows.map((row) => (
              <IncidentsTableRow
                columns={columns}
                isSelected={Boolean(rowSelection[row.id])}
                key={row.id}
                onClick={(selectedRow) => handleRowClick(selectedRow.id)}
                row={row}
              />
            ))}
          </TableBody>
          <TablePagination
            // `hasNextPage` describes the page that is currently rendered, and
            // it survives into the next page's load. Without the isLoading
            // guard a second click before the response lands would advance past
            // the real last page onto an empty one.
            hasNextPage={hasNextPage && !isLoading}
            onPageChange={(idx) => {
              setPaginationState((prev) => ({ ...prev, pageIndex: idx }))
              clearSelection()
            }}
            onPageSizeChange={(size) => {
              dispatch(setIncidentsPageSize(size))
              setPaginationState({ pageIndex: 0, pageSize: size })
              clearSelection()
            }}
            pageCount={pageCount}
            pageIndex={pageIndex}
            // `incidents` is the useLastData-retained list, so mid-page-change
            // it is the previous page's rows paired with the new pageIndex.
            // While loading the body renders pageSize skeletons, so report
            // that instead and the range matches what is on screen.
            pageRowCount={isLoading ? pageSize : incidents?.length ?? 0}
            pageSize={pageSize}
            pageSizeLabel={t('incidents:table_incidents_per_page')}
            total={totalIncidents}
          />
        </Table>
      </Box>

      {/* Summarize shares this bar, so it inherits `canBulkAction` — see the note in the legacy
          IncidentsTable. */}
      {isActionBarVisible && (
        <Box
          bottom='24px'
          left='50%'
          position='fixed'
          transform='translateX(-50%)'
          zIndex={10}
        >
          <ActionBar
            actions={[
              // Summarize sits before Bulk Resolve to match the legacy table's
              // ordering, where it renders ahead of the primary action.
              ...(isAiIncidentSummaryEnabled
                ? [
                    {
                      icon: <AiOperatorIcon strokeColor='currentColor' />,
                      label: t('incidents:summarize'),
                      onClick: () => {
                        trackEvent(AI_INCIDENT_SUMMARY_BUTTON_PRESS, {
                          incident_count: selectedIncidentIds.length,
                          view: 'rebranded',
                        })
                        onSummarizeModalOpen()
                      },
                    },
                  ]
                : []),
              {
                disabled: !canBulkResolve,
                disabledTooltipLabel: !canBulkResolve
                  ? t('incidents:bulk_resolve_active_only_tooltip')
                  : undefined,
                icon: <ResolveIcon color='currentColor' size={16} />,
                label: t('incidents:bulk_resolve'),
                onClick: onBulkModalOpen,
              },
            ]}
            colorScheme={colorScheme}
            onClearSelection={clearSelection}
            selectionLabel={`${selectedIncidentIds.length} ${
              selectedIncidentIds.length === 1
                ? t('incidents:table_incident_selected')
                : t('incidents:table_incidents_selected')
            }`}
          />
        </Box>
      )}

      <DarkBulkActionModal
        incidentIds={selectedIncidentIds}
        isOpen={isBulkModalOpen}
        onClose={onBulkModalClose}
        onSuccess={clearSelection}
      />

      {isAiIncidentSummaryEnabled && (
        <DarkSummarizeIncidentsModal
          incidents={selectedIncidents}
          isOpen={isSummarizeModalOpen}
          onClose={onSummarizeModalClose}
        />
      )}
    </>
  )
}
